import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Clock, DollarSign, Zap, TrendingUp, Calendar, User } from 'lucide-react';

interface AccelerationOpportunity {
  id: string;
  customerName: string;
  amount: number;
  daysToAccelerate: number;
  discount: number;
}

interface PaymentAccelerationData {
  totalOpportunity: string;
  avgDays: number;
  opportunities: AccelerationOpportunity[];
}

interface PaymentAccelerationWidgetProps {
  data: PaymentAccelerationData;
  onAction?: (action: string, data?: any) => void;
}

export const PaymentAccelerationWidget = ({ data, onAction }: PaymentAccelerationWidgetProps) => {
  const [selectedOpportunities, setSelectedOpportunities] = useState<string[]>([]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handleOpportunityToggle = (opportunityId: string) => {
    setSelectedOpportunities(prev => 
      prev.includes(opportunityId)
        ? prev.filter(id => id !== opportunityId)
        : [...prev, opportunityId]
    );
  };

  const handleAction = (action: string, actionData?: any) => {
    if (action === 'accelerate' && actionData) {
      onAction?.(action, { ...actionData, selected: selectedOpportunities });
    } else {
      onAction?.(action, actionData);
    }
  };

  const getDiscountColor = (discount: number) => {
    if (discount >= 2.5) return 'bg-success-muted text-success';
    if (discount >= 2.0) return 'bg-accent text-primary';
    return 'bg-warning-muted text-warning';
  };

  const getPriorityLevel = (daysToAccelerate: number) => {
    if (daysToAccelerate >= 15) return { label: 'High Impact', color: 'bg-success-muted text-success' };
    if (daysToAccelerate >= 10) return { label: 'Medium Impact', color: 'bg-accent text-primary' };
    return { label: 'Quick Win', color: 'bg-accent text-accent-foreground' };
  };

  const selectedTotal = data.opportunities
    .filter(opp => selectedOpportunities.includes(opp.id))
    .reduce((sum, opp) => sum + opp.amount, 0);

  const selectedSavings = data.opportunities
    .filter(opp => selectedOpportunities.includes(opp.id))
    .reduce((sum, opp) => sum + (opp.amount * opp.discount / 100), 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Payment Acceleration Opportunities</h3>
        <Badge variant="outline" className="text-primary bg-accent">
          <Zap className="h-3 w-3 mr-1" />
          {data.opportunities.length} opportunities
        </Badge>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-success" />
              <div>
                <p className="text-sm text-muted-foreground">Total Opportunity</p>
                <p className="text-xl font-bold text-foreground">{data.totalOpportunity}</p>
                <p className="text-xs text-muted-foreground">Across {data.opportunities.length} invoices</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Avg Acceleration</p>
                <p className="text-xl font-bold text-foreground">{data.avgDays} days</p>
                <p className="text-xs text-muted-foreground">Faster cash collection</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Selection Summary */}
      {selectedOpportunities.length > 0 && (
        <Card className="bg-accent border-primary/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-primary">
                  {selectedOpportunities.length} invoices selected
                </p>
                <p className="text-lg font-bold text-primary">
                  {formatCurrency(selectedTotal)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-primary">Estimated savings</p>
                <p className="text-lg font-bold text-success">
                  {formatCurrency(selectedSavings)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Opportunities List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top Acceleration Opportunities</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.opportunities.map((opp) => {
            const priority = getPriorityLevel(opp.daysToAccelerate);
            const savings = opp.amount * opp.discount / 100;
            
            return (
              <div key={opp.id} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-muted">
                <Checkbox
                  checked={selectedOpportunities.includes(opp.id)}
                  onCheckedChange={() => handleOpportunityToggle(opp.id)}
                />
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <p className="font-medium text-foreground">{opp.customerName}</p>
                    <Badge variant="outline" className={priority.color}>
                      {priority.label}
                    </Badge>
                  </div>
                  
                  <div className="flex gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      <span className="font-medium">{formatCurrency(opp.amount)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>{opp.daysToAccelerate} days faster</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      <span className="font-medium text-success">
                        {formatCurrency(savings)} savings
                      </span>
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <Badge className={getDiscountColor(opp.discount)}>
                    {opp.discount}% discount
                  </Badge>
                  <p className="text-xs text-muted-foreground mt-1">
                    Net: {formatCurrency(opp.amount - savings)}
                  </p>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button 
          className="flex-1"
          disabled={selectedOpportunities.length === 0}
          onClick={() => handleAction('accelerate', { selectedIds: selectedOpportunities })}
        >
          <Zap className="h-4 w-4 mr-2" />
          Accelerate Selected ({selectedOpportunities.length})
        </Button>
        <Button 
          variant="outline" 
          className="flex-1"
          onClick={() => setSelectedOpportunities(data.opportunities.map(o => o.id))}
        >
          Select All
        </Button>
        <Button 
          variant="outline"
          onClick={() => handleAction('details')}
        >
          View Details
        </Button>
      </div>

      {/* AI Insights Footer */}
      <div className="bg-accent rounded-lg p-3 border border-border">
        <div className="flex items-start gap-2">
          <div className="w-2 h-2 bg-accent0 rounded-full mt-2 flex-shrink-0"></div>
          <div>
            <p className="text-sm font-medium text-accent-foreground">Poniente & Full Send Analysis</p>
            <p className="text-sm text-accent-foreground">
              Optimal discount rates identified based on customer payment patterns. 
              Accelerating these invoices will improve your cash conversion cycle by 
              an average of {data.avgDays} days while maintaining strong customer relationships.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};